// NOTE: startTime/endTime come from GraphQL as AWSDateTime, generated as `any`.
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { DateTime } from 'luxon';
import type { MouseEvent } from 'react';
import type { ScheduledEvent, UntitledEvent } from './graphql';
import { EventType } from './graphql';

// Shared view model for events, used by both the card (board) and the table.

export type Status = 'live' | 'past' | 'upcoming';

export const STATUS: Record<Status, { label: string; className: string; icon?: string }> = {
  live: { label: 'Live', className: 'cc-red', icon: 'fas fa-circle' },
  upcoming: { label: 'Upcoming', className: 'cc-blue' },
  past: { label: 'Past', className: 'cc-neutral' }
};

const EVENT_TYPE: Partial<Record<EventType, { label: string; icon: string }>> = {
  [EventType.Produced]: { label: 'Produced', icon: 'far fa-camera-movie' },
  [EventType.SimLive]: { label: 'Sim Live', icon: 'far fa-calendar-day' },
  [EventType.Mobile]: { label: 'Mobile', icon: 'far fa-mobile' },
  [EventType.MobileKit]: { label: 'Mobile Kit', icon: 'far fa-mobile' },
  [EventType.Test]: { label: 'Test', icon: 'far fa-broadcast-tower' }
};

const DEFAULT_TYPE = { label: 'Static', icon: 'far fa-camera-home' };

export const eventTypeInfo = (type?: EventType): { label: string; icon: string } =>
  (type && EVENT_TYPE[type]) ?? DEFAULT_TYPE;

export interface EventView {
  /** Original scheduled event (for destinations); undefined for untitled events. */
  scheduled?: ScheduledEvent;
  cid: string;
  eid: string;
  to: string;
  title: string;
  organizationName?: string;
  thumbnail?: string;
  start: DateTime;
  end: DateTime;
  status: Status;
  supportNotes?: string;
  checkedUser?: string;
  /** Validator assigned to the event (event.verifier_email). */
  verifierEmail?: string;
  highPriority: boolean;
  /** Live/recording stream URL (mux for scheduled, stream_url for untitled). */
  muxStreamUrl?: string;
  receiveIsoRecordings?: boolean;
  /** Whether a videographer is assigned (event.operator_name). */
  hasVideographer?: boolean;
  /** Whether this is the client's first event (event.is_first_event). */
  isFirstEvent?: boolean;
  /** Whether an audio-mix cue sheet is set (event.audio_cue_sheet_url, non-empty). */
  hasAudioMix?: boolean;
}

const statusOf = (start: DateTime, isLive?: boolean | null): Status =>
  isLive ? 'live' : start > DateTime.now() ? 'upcoming' : 'past';

export const toEventView = (event: ScheduledEvent | UntitledEvent): EventView => {
  if ('eid' in event) {
    const startTime: string = event.startTime ?? '';
    const endTime: string = event.endTime ?? '';
    const start = DateTime.fromISO(startTime);
    const end = DateTime.fromISO(endTime);

    return {
      scheduled: event,
      cid: `${event.client?.cid ?? ''}.${event.location_secondary_id}`,
      eid: event.eid,
      to: `/event/${event.eid}`,
      title: event.name ?? event.client?.organizationName ?? 'Untitled',
      organizationName: event.client?.organizationName ?? undefined,
      thumbnail: event.backgroundUrl ?? event.client?.backgroundUrl ?? undefined,
      start,
      end,
      status: statusOf(start, event.isLive),
      supportNotes: event.support_notes,
      checkedUser: event.checked_user,
      verifierEmail: event.verifier_email,
      highPriority: event.high_priority ?? false,
      muxStreamUrl: event.muxStreamUrl ?? undefined,
      receiveIsoRecordings: Boolean(event.receive_iso_recordings),
      hasVideographer: Boolean(event.operator_name),
      isFirstEvent: Boolean(event.is_first_event),
      hasAudioMix: Boolean(event.audio_cue_sheet_url)
    };
  }

  const start = DateTime.fromISO(event.created_at ?? '');

  return {
    cid: '',
    eid: event.id.toString(),
    to: `/asset/${event.id}`,
    title: event.title ?? 'Untitled',
    thumbnail: event.thumbnail_url ?? undefined,
    start,
    end: start,
    status: statusOf(start, event.is_live),
    highPriority: false,
    muxStreamUrl: event.stream_url ?? undefined,
    receiveIsoRecordings: false
  };
};

// Formats e.g. "9:05 - 10:20 AM  ·  6/24".
export const formatTime = (start: DateTime, end: DateTime): string => {
  if (!start.isValid) {
    return '';
  }

  const time = start.toLocaleString(DateTime.TIME_SIMPLE);
  const endTime = end.isValid ? ` - ${end.toLocaleString(DateTime.TIME_SIMPLE)}` : '';
  const date = `  ·  ${start.toLocaleString(DateTime.DATE_SHORT)}`;

  return `${time}${endTime}${date}`;
};

// Prevents a surrounding link/row from navigating when a control is clicked.
export const stopNav = (e: MouseEvent): void => {
  e.preventDefault();
  e.stopPropagation();
};
